import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';

export const usePlansApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  // SuperAdmin: Plan CRUD
  const getPlans = () =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.list);

  const getPlan = (id: string) =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.get(id));

  const createPlan = (data: any) =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.plans.create, data);

  const updatePlan = (id: string, data: any) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.plans.update(id), data);

  const deletePlan = (id: string) =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.plans.delete(id));

  /** Super-admin: persist a new plan ordering (drag-and-drop) — sets sortOrder from the id sequence. */
  const reorderPlans = (orderedIds: string[]) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.plans.reorder, { orderedIds });

  /** Super-admin: set a plan's monthly price → creates a new Stripe price on its product. */
  const setPlanStripePrice = (id: string, amount: number, currency?: string) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.billing.adminPlanStripePrice(id), { amount, currency });

  // Vendor: My Plan
  const getMyPlan = () =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.myPlan);

  const changeMyPlan = (planId: string) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.plans.changeMyPlan, { planId });

  // Super-admin: pricing v2 — rate card, trials, contracts.
  const getRateCard = () =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.rateCard);

  /** `{ extendDays }`, `{ endNow: true }`, `{ endsAt }` or `{ endsAt: null }` (open-ended). */
  const setVendorTrial = (vendorId: string, body: { extendDays?: number; endNow?: boolean; endsAt?: string | null }) =>
    sendAuthGuardedRequest(ApiMethod.PATCH, routes.plans.vendorTrial(vendorId), body);

  const getVendorContract = (vendorId: string) =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.vendorContract(vendorId));

  const saveVendorContract = (vendorId: string, contract: any) =>
    sendAuthGuardedRequest(ApiMethod.PUT, routes.plans.vendorContract(vendorId), contract);

  const getVendorOffer = (vendorId: string) =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.plans.vendorOffer(vendorId));

  /** `offer: null` removes it: the vendor sees list prices again. */
  const saveVendorOffer = (vendorId: string, offer: any | null) =>
    sendAuthGuardedRequest(ApiMethod.PUT, routes.plans.vendorOffer(vendorId), { offer });

  return {
    getVendorOffer,
    saveVendorOffer,
    getRateCard,
    setVendorTrial,
    getVendorContract,
    saveVendorContract,
    getPlans,
    getPlan,
    createPlan,
    updatePlan,
    deletePlan,
    reorderPlans,
    setPlanStripePrice,
    getMyPlan,
    changeMyPlan,
  };
};
